/**
 * MediaPipe Face Landmarker indices (478 landmarks: 468 face mesh + 10 iris).
 * "Left"/"right" follow MediaPipe's naming (subject's side in a non-mirrored
 * image). The pipeline only uses symmetric pairs, so mirroring never matters.
 */
export const LM = {
  NOSE_TIP: 1,
  NOSE_BRIDGE_TOP: 6, // between the eyes, on the nose bridge
  NASION: 168, // slightly above 6, between the eyebrows' inner ends
  NOSE_MID: 197,
  CHIN: 152,
  FOREHEAD: 10,

  RIGHT_EYE_OUTER: 33,
  RIGHT_EYE_INNER: 133,
  LEFT_EYE_INNER: 362,
  LEFT_EYE_OUTER: 263,

  RIGHT_EYE_TOP: 159,
  RIGHT_EYE_BOTTOM: 145,
  LEFT_EYE_TOP: 386,
  LEFT_EYE_BOTTOM: 374,

  // Side of the head at eye level (roughly where temples pass) and near the ears.
  RIGHT_TEMPLE: 127,
  LEFT_TEMPLE: 356,
  RIGHT_EAR: 234,
  LEFT_EAR: 454,
  RIGHT_CHEEK: 234,
  LEFT_CHEEK: 454,

  // Iris: centre + 4 boundary points (1&3 opposite horizontally, 2&4 vertically).
  RIGHT_IRIS_CENTER: 468,
  RIGHT_IRIS: [469, 470, 471, 472] as const,
  LEFT_IRIS_CENTER: 473,
  LEFT_IRIS: [474, 475, 476, 477] as const,
} as const;

export const LANDMARK_COUNT = 478;

/** Flat Float32Array layout: [x0, y0, z0, x1, y1, z1, ...] normalised to [0,1]. */
export type LandmarkBuffer = Float32Array;

export function lmX(buf: LandmarkBuffer, i: number): number {
  return buf[i * 3]!;
}
export function lmY(buf: LandmarkBuffer, i: number): number {
  return buf[i * 3 + 1]!;
}
export function lmZ(buf: LandmarkBuffer, i: number): number {
  return buf[i * 3 + 2]!;
}

/** Pixel distance between two landmarks. */
export function pixelDistance(buf: LandmarkBuffer, a: number, b: number, width: number, height: number): number {
  const dx = (lmX(buf, a) - lmX(buf, b)) * width;
  const dy = (lmY(buf, a) - lmY(buf, b)) * height;
  return Math.hypot(dx, dy);
}

/** Axis-aligned bounding box of all landmarks, in pixels. */
export function landmarkBounds(buf: LandmarkBuffer, width: number, height: number) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (let i = 0; i < LANDMARK_COUNT; i++) {
    const x = buf[i * 3]!;
    const y = buf[i * 3 + 1]!;
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  return { x: minX * width, y: minY * height, w: (maxX - minX) * width, h: (maxY - minY) * height };
}
